// "Your engine": the sales process (process/steps.yaml, via process.generated.ts) drawn as hubs of activity joined by
// cables. This file is the one place to edit what each hub is called, what it says, which phases (and so which steps)
// it covers, its channel satellites, its recommended set-up and its content ideas. Pure data and pure functions, so the
// map, the panel and the tests share it. Each hub belongs to one module of the system map (docs/SYSTEM_MAP.md):
// Attract, Find and Network bring people in, Connect turns interest into a sale, ReConnect keeps the rest warm.
import { PROCESS } from "./process.generated";
import type { ProcessStep, StepLive } from "./process";
import type { JsonObject, VendorStatus } from "./types";
import type { Crm } from "./crm";
import { hasIntake } from "./onboarding";
import { ROUTES, type RouteKey } from "./routes";

export type HubId = "core" | "attract" | "network" | "find" | "know" | "reach" | "listen" | "book" | "meet" | "win" | "nurture" | "engine";

/** The modules of the system map (docs/SYSTEM_MAP.md). "shared" is the CRM, Listen and the engine room. */
export type ModuleId = "attract" | "find" | "network" | "connect" | "reconnect" | "complete" | "serve" | "shared";

export interface Module {
  id: ModuleId;
  /** The product name, for example "Ogma Find". */
  name: string;
  /** The short name used on the map and in the menu. */
  short: string;
  tagline: string;
  comingSoon?: boolean;
}

export const MODULES: Module[] = [
  { id: "attract", name: "Ogma Attract", short: "Attract", tagline: "People come to you" },
  { id: "find", name: "Ogma Find", short: "Find", tagline: "You go to them" },
  { id: "network", name: "Ogma Network", short: "Network", tagline: "Relationships do the work" },
  { id: "connect", name: "Ogma Connect", short: "Connect", tagline: "Interest becomes a sale" },
  { id: "reconnect", name: "Ogma ReConnect", short: "ReConnect", tagline: "Keep warm and bring back" },
  { id: "complete", name: "Ogma Complete", short: "Complete", tagline: "Contract, invoice and hand-over", comingSoon: true },
  { id: "serve", name: "Ogma Serve", short: "Serve", tagline: "Looking after customers", comingSoon: true },
  { id: "shared", name: "Shared", short: "Shared", tagline: "Underneath every module" },
];

export const MODULE_BY_ID = Object.fromEntries(MODULES.map((m) => [m.id, m])) as Record<ModuleId, Module>;

/** Where a hub sits in the story: what colour its light is and which side of the map it lives on. */
export type HubKind = "inbound" | "outbound" | "flow" | "win" | "loop" | "core" | "engine";

export interface Satellite {
  id: string;
  label: string;
  /** A shorter label for the map, where space is tight. */
  short?: string;
  /** Shown in the panel and read out to screen readers. */
  detail?: string;
  /** The steps this channel or source covers; it dims when none of them applies to the business. */
  steps: string[];
  /** The engine switch (E3) that pauses it, when it is not `<hub>:<id>` (Attract's routes share switches). */
  switchKey?: string;
}

/** One E3 switch inside a station: a channel or source, or for Attract a group of routes. */
export interface ChannelSwitch {
  key: string;
  label: string;
  steps: string[];
  /** The satellites it pauses. */
  satellites: string[];
}

/** What the set-up checks look at. Gathered once on the engine page (Engine.tsx) from reads the app already makes. */
export interface EngineFacts {
  onboarded: boolean;
  intake: boolean;
  configApproved: boolean;
  accountsRequired: number;
  accountsReady: number;
  /** Outside accounts on the checklist for this business. A vendor not listed is not needed. */
  vendors: Record<string, VendorStatus>;
  config: JsonObject | null;
  tagKeys: string[];
  sequences: number;
  articles: number;
  /** crm_process_map rows by step id (empty until settings are approved, or if the read fails). */
  live: Map<string, StepLive>;
  /** 0.12 At least one Instagram or Facebook account is connected through Meta (crm_meta_accounts). */
  metaConnected?: boolean;
}

export interface SetupItem {
  id: string;
  label: string;
  hint: string;
  /** A route in the app, optionally with a #section. */
  to?: string;
  comingSoon?: boolean;
  /** true done, false to do, null does not apply to this business (hidden). Coming-soon items have no check. */
  check?: (f: EngineFacts) => boolean | null;
}

export interface ContentIdea {
  label: string;
  hint: string;
  to?: string;
}

export interface Hub {
  id: HubId;
  /** The station's reference on the plan, for example ST-03. */
  ref: string;
  name: string;
  /** A few words under the name on the map. */
  tagline: string;
  /** What this part does, in plain words (the panel's opening line). */
  description: string;
  phases: string[];
  /** Steps this hub takes from other phases (Network takes referral and review steps from P2, P6 and P10). */
  claims?: string[];
  module: ModuleId;
  kind: HubKind;
  satellites: Satellite[];
  setup: SetupItem[];
  content: ContentIdea[];
}

// ---------------------------------------------------------------------------
// Checks
// ---------------------------------------------------------------------------
const READY: VendorStatus[] = ["verified", "credentials_added", "not_needed"];

/** Done when the vendor is connected; null when this business does not need it. */
export function vendorReady(f: EngineFacts, key: string): boolean | null {
  const s = f.vendors[key];
  if (s === undefined) return f.configApproved ? null : false;
  if (s === "not_needed") return null;
  return READY.includes(s);
}

/** 0.8: the sending mailbox is connected on Google Workspace or Outlook / Microsoft 365 (either one is enough). */
export function mailboxReady(f: EngineFacts): boolean | null {
  const g = vendorReady(f, "google_workspace");
  const m = vendorReady(f, "microsoft_365");
  if (g === true || m === true) return true;
  if (g === null && m === null) return null;
  return false;
}

function has(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return Boolean(v);
}

function cfg(f: EngineFacts, ...path: string[]): unknown {
  let v: unknown = f.config;
  for (const k of path) {
    if (v === null || typeof v !== "object") return undefined;
    v = (v as Record<string, unknown>)[k];
  }
  return v;
}

/** Events in the last 30 days across a set of phases. */
export function activity(f: Pick<EngineFacts, "live">, phases: string[]): number {
  let n = 0;
  for (const s of PROCESS.steps) if (phases.includes(s.phase)) n += f.live.get(s.id)?.events_30d ?? 0;
  return n;
}

// ---------------------------------------------------------------------------
// Attract: its satellites are the route catalogue (routes.ts, from engine/routes.py); each route is paused by the
// Attract switch the catalogue names. The switches keep their keys (engine/switches.py CHANNEL_STEPS and
// crm__engine_switch_keys), so several routes share one: LinkedIn, Instagram and Facebook, and Video are all
// "social"; Blog and PR are "content". DMs keep their own switch (step 1.25, the Instagram and Facebook route).
// engine/tests/test_switches.py reads the lines below.
// ---------------------------------------------------------------------------
export const ATTRACT_SWITCHES: { id: string; label: string; steps: string[] }[] = [
  { id: "content", label: "Content and blog", steps: ["1.2", "1.3", "1.7", "1.8", "1.34", "1.37"] },
  { id: "social", label: "Social", steps: ["1.4", "1.6", "1.16", "1.20"] },
  { id: "website", label: "Website and forms", steps: ["1.11", "1.12", "1.13", "1.14"] },
  { id: "newsletter", label: "Newsletter", steps: ["1.9", "1.15", "1.56"] },
  { id: "ads", label: "Ads", steps: ["1.10", "1.26", "1.27", "1.29", "1.30", "1.31", "1.48"] },
  { id: "dms", label: "DMs", steps: ["1.25"] },
  { id: "listings", label: "Get found", steps: ["1.42", "1.43", "1.44", "1.45", "1.46", "1.47", "1.57"] },
  { id: "print", label: "Print", steps: ["1.58", "1.59", "1.60"] },
];

/**
 * #513 Switches a module's Rules own (engine/switches.py RULES_OWNED): the newsletter runs on newsletter.enabled, set
 * in Attract › Rules. The engine pages report them with a link there and offer no pause, and a pause saved before is
 * ignored (crm.engineSwitches leaves these keys out), so no business is stuck paused with no button.
 */
export const RULES_SWITCHES: Record<string, { where: string; to: string; on: (config: JsonObject | null) => boolean }> = {
  "attract:newsletter": {
    where: "Attract › Rules",
    to: "/attract/rules/volume",
    on: (config) => {
      const news = config?.newsletter;
      return !(typeof news === "object" && news !== null && !Array.isArray(news) && news.enabled === false);
    },
  },
};

/** "On" or "Off in Attract › Rules": a Rules-owned switch as the engine pages say it. */
export function rulesSwitchWords(key: string, config: JsonObject | null): string | null {
  const r = RULES_SWITCHES[key];
  return r ? (r.on(config) ? "On" : `Off in ${r.where}`) : null;
}

/** Short names for the map, where space is tight. */
const ROUTE_SHORT: Partial<Record<RouteKey, string>> = { blog: "Blog", meta: "Instagram, Facebook", print: "Print", listings: "Listings", pr: "PR and media" };

const switchSteps = (key: string) => ATTRACT_SWITCHES.find((s) => `attract:${s.id}` === key)?.steps ?? [];

const ATTRACT_ROUTES: Satellite[] = ROUTES.map((r) => ({
  id: r.key,
  label: r.label,
  short: ROUTE_SHORT[r.key],
  detail: `${r.who}.`,
  steps: switchSteps(r.switch),
  switchKey: r.switch,
}));

/** The E3 switch key that pauses a satellite. */
export function switchKeyOf(hub: HubId, sat: Pick<Satellite, "id" | "switchKey">): string {
  return sat.switchKey ?? `${hub}:${sat.id}`;
}

// ---------------------------------------------------------------------------
// The hubs
// ---------------------------------------------------------------------------
export const HUBS: Hub[] = [
  {
    id: "core",
    ref: "ST-00",
    name: "Your CRM",
    tagline: "The memory",
    description:
      "Everything the engine learns lands here: every firm, person, message, meeting and deal. Each part of the engine reads from it before acting and writes back what happened, so nothing is forgotten. Set-up starts here, with your playbook.",
    phases: ["P0"],
    module: "shared",
    kind: "core",
    satellites: [],
    setup: [
      { id: "intake", label: "Tell us about your business", hint: "What you sell, who buys it and how you win work today.", to: "/setup/core", check: (f) => f.intake || f.configApproved },
      { id: "playbook", label: "Approve your first offer", hint: "Drafted from your website. Nothing runs until you approve it.", to: "/setup/running", check: (f) => f.configApproved },
      {
        id: "accounts",
        label: "Connect your accounts and store your keys",
        hint: "Email, data and sending services. Keys go straight into a locked vault.",
        to: "/setup/connect",
        check: (f) => f.configApproved && f.accountsReady >= f.accountsRequired,
      },
      { id: "finish", label: "Finish set-up", hint: "Switch the engine on. You can connect the rest later.", to: "/setup/live", check: (f) => f.onboarded },
    ],
    content: [
      { label: "Ask Ogma a question", hint: "For example, “firms I haven't contacted in Glasgow”.", to: "/ask" },
      { label: "Read your playbook once a month", hint: "The agents follow it to the letter, so keep it current.", to: "/settings/business/playbook" },
    ],
  },
  {
    id: "attract",
    ref: "ST-01",
    name: "Attract",
    tagline: "People come to you",
    description:
      "Articles, posts, the newsletter and your website bring people to you. The engine researches what works in your market and drafts the content; you approve every post before it goes out. When someone gets in touch, their enquiry goes straight to Connect, to be answered quickly and booked.",
    phases: ["P1"],
    module: "attract",
    kind: "inbound",
    // The ten routes of the catalogue (routes.ts), in order.
    satellites: ATTRACT_ROUTES,
    setup: [
      { id: "social", label: "Set your social networks", hint: "Which networks you post on, and the account that schedules posts.", to: "/setup/parts/attract", check: (f) => has(cfg(f, "social")) || has(cfg(f, "tools", "social")) || vendorReady(f, "buffer") === true || f.metaConnected === true },
      { id: "article", label: "Write your first pillar article", hint: "One long, useful piece that posts and the newsletter are cut from.", to: "/attract/actions/long-form", check: (f) => f.articles > 0 },
      { id: "newsletter", label: "Set up your newsletter", hint: "Only if your playbook includes one.", to: "/setup/parts/attract", check: (f) => vendorReady(f, "resend") },
    ],
    content: [
      { label: "Write your first pillar article", hint: "Answer the question your customers ask most. The engine fact-checks it.", to: "/attract/actions/long-form" },
      { label: "Set your social networks", hint: "Pick one or two networks where your buyers really are.", to: "/settings/business/playbook" },
      { label: "Turn one article into five posts", hint: "Each post comes to you for approval first.", to: "/attract/actions/posts" },
    ],
  },
  {
    id: "find",
    ref: "ST-02",
    name: "Find",
    tagline: "You go to them",
    description:
      "The engine looks for firms worth contacting: public registers and Companies House, maps and directories, news, job posts and tenders. It checks every firm against your do-not-contact list before adding it, then Find researches each one and reaches out.",
    phases: ["P2"],
    module: "find",
    kind: "outbound",
    satellites: [
      { id: "registers", label: "Registers and Companies House", short: "Companies House", detail: "Industry registers, filings and filing events.", steps: ["2.1", "2.2", "2.3"] },
      { id: "maps", label: "Maps and directories", detail: "Map listings and trade directories.", steps: ["2.13", "2.14", "2.15"] },
      { id: "signals", label: "News and signals", detail: "News, job posts, tenders and deal alerts.", steps: ["2.4", "2.5", "2.6", "2.9", "2.10"] },
    ],
    setup: [
      { id: "companies-house", label: "Connect Companies House", hint: "A free key that lets the engine read filings and find owners.", to: "/setup/parts/find", check: (f) => vendorReady(f, "companies_house") },
      { id: "where", label: "Choose where to look", hint: "Your search terms, registers and directories, in your playbook.", to: "/settings/business/playbook", check: (f) => has(cfg(f, "target")) || has(cfg(f, "lead_sources")) || has(cfg(f, "ideal_customer", "sectors")) },
    ],
    content: [
      { label: "Name three firms you would love to work with", hint: "The engine looks for more like them.", to: "/contacts/find" },
      { label: "See which buyers have contracts ending soon", hint: "Tenders are early warnings: get known before the next one.", to: "/contacts/find/news" },
      { label: "Import your existing contacts", hint: "Bring in a spreadsheet of people you already know, or your HubSpot or Pipedrive.", to: "/settings/data" },
    ],
  },
  {
    id: "know",
    ref: "ST-03",
    name: "Research",
    tagline: "Research and score",
    description:
      "For each firm, the engine finds the right person, checks their email, researches the business and writes a personal opening line. Then it scores how good a fit they are, so the best ones are contacted first.",
    phases: ["P3"],
    module: "find",
    kind: "flow",
    satellites: [],
    setup: [
      { id: "icp", label: "Check who your ideal customer is", hint: "Scores are only as good as this description.", to: "/settings/business/playbook", check: (f) => has(cfg(f, "ideal_customer")) },
      { id: "tags", label: "Set up tags for people", hint: "For example client, introducer or supplier.", to: "/network/accounts/tags", check: (f) => f.tagKeys.length > 0 },
    ],
    content: [
      { label: "Tag your five best customers", hint: "The engine learns what a good fit looks like.", to: "/contacts/people" },
      { label: "Check the scores on your top firms", hint: "If a score looks wrong, tell the assistant why.", to: "/results/sales" },
    ],
  },
  {
    id: "reach",
    ref: "ST-04",
    name: "Outreach",
    tagline: "Every channel",
    description:
      "The engine picks the right mix of channels for each person, writes every message in your voice and sends within safe daily limits. Nothing goes out until you approve the batch.",
    phases: ["P4"],
    module: "find",
    kind: "flow",
    satellites: [
      { id: "email", label: "Email", steps: ["4.3"] },
      { id: "letter", label: "Letter", detail: "Handwritten-style letters, posted for you.", steps: ["4.4"] },
      { id: "linkedin", label: "LinkedIn", steps: ["4.5"] },
      { id: "phone", label: "Phone", steps: ["4.7"] },
      { id: "video", label: "Video", detail: "Short personal videos.", steps: ["4.6"] },
      { id: "whatsapp", label: "WhatsApp", detail: "Only to people who have opted in.", steps: ["4.9"] },
    ],
    setup: [
      { id: "gmail", label: "Connect your mailbox", hint: "Google Workspace or Outlook / Microsoft 365. Messages go from your own mailbox, so replies come back to you.", to: "/setup/parts/find", check: (f) => mailboxReady(f) },
      { id: "voice", label: "Set your brand and voice", hint: "Your colours, tone and words to avoid.", to: "/settings/business/playbook", check: (f) => has(cfg(f, "voice")) && has(cfg(f, "brand")) },
      { id: "sequences", label: "Review your sequences", hint: "Which touches go out, in what order, on which channel.", to: "/settings/business/playbook", check: (f) => f.sequences > 0 || has(cfg(f, "sequences")) },
      { id: "approvals", label: "Choose what needs your approval", hint: "Start by approving everything; loosen it as you build trust.", to: "/settings/approvals#autonomy-section", check: (f) => f.configApproved && Array.isArray(cfg(f, "human_gates")) },
    ],
    content: [
      { label: "Review your first sequence", hint: "Read it as your customer would.", to: "/settings/business/playbook" },
      { label: "Choose letter or email first", hint: "A letter stands out with owners who get a lot of email.", to: "/settings/business/playbook" },
      { label: "Approve your first batch", hint: "Every draft shows why it was written that way.", to: "/campaigns/to-approve" },
    ],
  },
  {
    id: "listen",
    ref: "ST-05",
    name: "Replies",
    tagline: "Every answer caught",
    description:
      "Every enquiry from Attract, every introduction from Network and every reply to Find's outreach lands here. Opens, clicks and website visits are caught and matched to the right person. The engine works out what each message means: interest goes on into Connect and is answered at that lead's pace, people not ready yet go to ReConnect, and anything unclear comes to you.",
    phases: ["P5", "P6"],
    module: "connect",
    kind: "flow",
    satellites: [],
    setup: [
      { id: "inbox", label: "Connect your inbox", hint: "The same mailbox (Gmail or Outlook), so replies are caught straight away.", to: "/setup/parts/find", check: (f) => mailboxReady(f) },
      { id: "objections", label: "Write answers to common objections", hint: "The engine uses your words when someone pushes back.", to: "/settings/business/playbook", check: (f) => has(cfg(f, "objections")) || has(cfg(f, "voice", "objections")) },
    ],
    content: [
      { label: "Answer the replies waiting for you", hint: "Unclear replies wait in your inbox.", to: "/conversations/inbox" },
      { label: "Note how you answer your top three objections", hint: "Tell the assistant; it becomes part of your playbook.", to: "/settings/business/playbook" },
    ],
  },
  {
    id: "book",
    ref: "ST-06",
    name: "Booking",
    tagline: "Meetings in the diary",
    description: "When someone is interested, the engine offers two times, puts the meeting in your calendar and sends a short pre-read and a reminder.",
    phases: ["P7"],
    module: "connect",
    kind: "flow",
    satellites: [],
    setup: [
      { id: "calendar", label: "Connect your calendar", hint: "Google Calendar or Outlook, through the same mailbox connection.", to: "/setup/parts/connect", check: (f) => mailboxReady(f) },
      { id: "ask", label: "Say what you are asking for", hint: "For example a 20-minute call. It is in your playbook's offer.", to: "/settings/business/playbook", check: (f) => has(cfg(f, "offer", "ask")) },
    ],
    content: [{ label: "Check this week's meetings", hint: "Each one has a brief ready before you go in.", to: "/conversations/meetings" }],
  },
  {
    id: "meet",
    ref: "ST-07",
    name: "Meetings",
    tagline: "You do the talking",
    description: "Before each meeting you get a short brief. Afterwards the engine checks it happened, captures the notes and pulls out the next steps.",
    phases: ["P8"],
    module: "connect",
    kind: "flow",
    satellites: [],
    setup: [
      { id: "first", label: "Mark your first meeting", hint: "Say whether it happened and paste the notes.", to: "/conversations/meetings", check: (f) => activity(f, ["P8"]) > 0 },
      { id: "notetaker", label: "Connect a note-taker", hint: "Notes captured automatically from your calls.", comingSoon: true },
    ],
    content: [{ label: "Paste notes after each meeting", hint: "Next steps are pulled out for you.", to: "/conversations/meetings" }],
  },
  {
    id: "win",
    ref: "ST-08",
    name: "Quotes and deals",
    tagline: "They become customers",
    description:
      "A good meeting becomes a deal: recap, NDA or proposal, the information you need, then the quote and a decision. You approve every offer. A won deal is marked won (Ogma Complete and Ogma Serve are coming soon); a lost one goes to ReConnect with its reason.",
    phases: ["P9"],
    module: "connect",
    kind: "win",
    satellites: [],
    setup: [
      { id: "esign", label: "Connect e-signing", hint: "NDAs and proposals signed online.", to: "/setup/parts/connect", check: (f) => vendorReady(f, "documenso") },
      { id: "documents", label: "Choose which documents need you", hint: "Which documents wait for your approval before they are sent.", to: "/settings/business/playbook", check: (f) => has(cfg(f, "documents")) },
    ],
    content: [
      { label: "See your deals", hint: "Every deal and what happens next.", to: "/conversations/deals" },
      { label: "Write down why you lost the last deal", hint: "The engine learns from losses as well as wins.", to: "/conversations/deals" },
    ],
  },
  {
    id: "nurture",
    ref: "ST-09",
    name: "ReConnect",
    tagline: "Keep warm, bring back",
    description:
      "Everyone who leaves Connect without buying, and everyone who said \"not now\", stays in touch through useful notes. When they show interest again (a reply, a booking link clicked, a revisit, a contract of theirs coming up for tender) they go back into Connect with their full history. The engine also learns what works.",
    phases: ["P10"],
    module: "reconnect",
    kind: "loop",
    satellites: [],
    setup: [
      { id: "rhythm", label: "Decide how often to keep in touch", hint: "Tell the assistant; it becomes part of your playbook.", to: "/settings/business/playbook", check: (f) => has(cfg(f, "nurture")) || vendorReady(f, "resend") === true },
    ],
    content: [
      { label: "See who you are keeping warm", hint: "Everyone not ready yet, and the deals you lost and why.", to: "/reconnect/research/not-ready" },
      { label: "Send a market note to people not ready yet", hint: "Short, useful and no hard sell.", to: "/attract/actions/newsletter" },
    ],
  },
  {
    id: "network",
    ref: "ST-11",
    name: "Network",
    tagline: "Relationships do the work",
    description:
      "The people who already know you bring you work: introducers and partners, customers asked for referrals and reviews, and the events where your buyers gather. Introductions go straight to Connect, at the slowest pace: the relationship comes before any offer.",
    phases: [],
    claims: ["2.7", "2.8", "6.7", "10.3", "10.4", "10.5", "10.19", "2.22", "2.23", "2.24", "2.25", "2.26", "2.27", "2.28", "2.29"],
    module: "network",
    kind: "inbound",
    satellites: [
      { id: "intros", label: "Introducers and partners", short: "Introducers", detail: "People who send you work, and the introductions they make.", steps: ["2.7", "2.8"] },
      { id: "referrals", label: "Referrals", detail: "Asking happy customers and contacts for referrals, and thanking referrers.", steps: ["6.7", "10.3", "10.4"] },
      { id: "reviews", label: "Reviews", detail: "Asking every customer how it went, then for a review at the right moment.", steps: ["10.5", "10.19"] },
      { id: "events", label: "Events and speaking", short: "Events", detail: "Events your buyers go to: found, scored, chosen by you, pitched, met and followed up.", steps: ["2.22", "2.23", "2.24", "2.25", "2.26", "2.27", "2.28", "2.29"] },
    ],
    setup: [
      { id: "referrers", label: "Tag your introducers and referrers", hint: "So the engine thanks them and keeps them close.", to: "/network/accounts/tags", check: (f) => f.tagKeys.some((k) => /introduc|referr/.test(k)) },
      {
        id: "events",
        label: "Say how far you will go for events",
        hint: "Your topics, markets, travel distance and cost limit per event, so the weekly scan finds the right rooms.",
        to: "/network/rules/events-reach",
        check: (f) => (cfg(f, "network", "events", "enabled") === true ? has(cfg(f, "network", "events", "keywords")) : null),
      },
    ],
    content: [
      { label: "List the people who introduce you to customers", hint: "Tag them as introducers so the engine keeps in touch.", to: "/network/research/introducers" },
      { label: "Ask a happy customer for a review", hint: "The engine drafts the request for you.", to: "/network/actions/reviews" },
      { label: "Pick two events your buyers go to", hint: "The engine finds and scores events each week; you choose which to go to or speak at.", to: "/network/research/events" },
    ],
  },
  {
    id: "engine",
    ref: "ST-10",
    name: "Engine room",
    tagline: "Agents at work",
    description:
      "The agents that run in the background: schedules, safety limits, bounce and spam checks, data rules and alerts. It keeps the engine safe and tells you when something needs a look.",
    phases: ["E"],
    module: "shared",
    kind: "engine",
    satellites: [],
    setup: [
      { id: "caps", label: "Set your spending caps", hint: "A daily and monthly limit for AI and paid services.", to: "/settings/approvals#budget-section", check: (f) => has(cfg(f, "engine")) || has(cfg(f, "budget")) },
      { id: "autonomy", label: "See what runs by itself", hint: "Which steps run alone and which wait for you.", to: "/settings/approvals#autonomy-section", check: (f) => f.configApproved },
    ],
    content: [
      { label: "See how it all works", hint: "Every step, who does it and what is connected.", to: "/settings/business/playbook?tab=process" },
      { label: "Look at what the AI costs", hint: "Spend this month against your caps.", to: "/settings/approvals#budget-section" },
    ],
  },
];

export const HUB_BY_ID = Object.fromEntries(HUBS.map((h) => [h.id, h])) as Record<HubId, Hub>;

/** The channel switches a station's panel offers: one per satellite, or for Attract one per switch, naming its routes. */
export function hubSwitches(hub: Hub): ChannelSwitch[] {
  if (hub.id === "attract") {
    return ATTRACT_SWITCHES.map((s) => {
      const key = `attract:${s.id}`;
      const routes = hub.satellites.filter((x) => switchKeyOf(hub.id, x) === key);
      const label = s.id === "dms" ? "DMs (Instagram and Facebook)" : routes.length ? routes.map((r) => r.label).join(", ") : s.label;
      return { key, label, steps: s.steps, satellites: routes.map((r) => r.id) };
    });
  }
  return hub.satellites.map((s) => ({ key: switchKeyOf(hub.id, s), label: s.label, steps: s.steps, satellites: [s.id] }));
}

/**
 * How the stations are wired. Inbound interest (Attract) and introductions (Network) go straight to Listen and reply.
 * Outbound (Find) runs Find → Know them → Reach out → Listen and reply. Interest then goes through Connect (Book, Meet,
 * Quote and win). Anyone not ready yet goes to ReConnect, which hands them back to Listen and reply, and so into
 * Connect, when their interest returns. The map's cables follow this list (a test checks).
 */
export const WIRING: { from: HubId; to: HubId; kind: "inbound" | "outbound" | "flow" | "loop" }[] = [
  { from: "attract", to: "listen", kind: "inbound" },
  { from: "network", to: "listen", kind: "inbound" },
  { from: "find", to: "know", kind: "outbound" },
  { from: "know", to: "reach", kind: "outbound" },
  { from: "reach", to: "listen", kind: "outbound" },
  { from: "listen", to: "book", kind: "flow" },
  { from: "book", to: "meet", kind: "flow" },
  { from: "meet", to: "win", kind: "flow" },
  { from: "listen", to: "nurture", kind: "loop" },
  { from: "win", to: "nurture", kind: "loop" },
  { from: "nurture", to: "listen", kind: "loop" },
];

/** Where a station sends people next, in plain words for the text-only list. */
export function leadsTo(id: HubId): Hub[] {
  return WIRING.filter((w) => w.from === id).map((w) => HUB_BY_ID[w.to]);
}

/** The order to set things up in. "Start here" points at the first hub in this list with something left to do. */
export const SETUP_ORDER: HubId[] = ["core", "know", "find", "reach", "listen", "book", "attract", "network", "nurture"];

/** Human gates in plain words, by step. Shown as locks on the cables (engineGeometry.ts places them). */
export const GATE_WORDS: Record<string, string> = {
  "0.5": "You approve the playbook",
  "1.21": "You approve each post",
  "4.10": "You approve each batch of messages",
  "6.11": "You decide unclear replies",
  "8.3": "You hold the meeting",
  "9.6": "You approve every offer",
};

// ---------------------------------------------------------------------------
// Steps and status
// ---------------------------------------------------------------------------
export function hubForPhase(phase: string): Hub | undefined {
  return HUBS.find((h) => h.phases.includes(phase));
}

/** The hub a step belongs to: the hub that claims it, otherwise the home of its phase. */
export function hubForStep(step: Pick<ProcessStep, "id" | "phase">): Hub | undefined {
  return HUBS.find((h) => h.claims?.includes(step.id)) ?? hubForPhase(step.phase);
}

export function hubSteps(hub: Hub): ProcessStep[] {
  return PROCESS.steps.filter((s) => hubForStep(s) === hub);
}

/** The gates on for this business: its human_gates, then crm_process_map, then steps.yaml. */
export function activeGates(f: Pick<EngineFacts, "config" | "live">): string[] {
  const listed = f.config?.human_gates;
  const fromConfig = Array.isArray(listed) ? listed.map(String) : null;
  return Object.keys(GATE_WORDS).filter((id) => {
    if (fromConfig) return fromConfig.includes(id) || (id === "8.3" && Boolean(PROCESS.steps.find((s) => s.id === id)?.human));
    const live = f.live.get(id);
    if (live) return live.human_gate;
    return Boolean(PROCESS.steps.find((s) => s.id === id)?.human);
  });
}

export interface SetupState {
  item: SetupItem;
  /** "done", "todo", or "soon" (coming soon). */
  state: "done" | "todo" | "soon";
}

/** The set-up items that apply to this business, with done or not. */
export function hubSetup(hub: Hub, f: EngineFacts): SetupState[] {
  const out: SetupState[] = [];
  for (const item of hub.setup) {
    if (item.comingSoon || !item.check) {
      out.push({ item, state: "soon" });
      continue;
    }
    let r: boolean | null;
    try {
      r = item.check(f);
    } catch {
      r = false;
    }
    if (r === null) continue;
    out.push({ item, state: r ? "done" : "todo" });
  }
  return out;
}

export interface HubProgress {
  done: number;
  total: number;
  /** 0 to 1; a hub with nothing to set up counts as complete. */
  ratio: number;
  complete: boolean;
}

export function hubProgress(hub: Hub, f: EngineFacts): HubProgress {
  const items = hubSetup(hub, f).filter((s) => s.state !== "soon");
  const done = items.filter((s) => s.state === "done").length;
  const total = items.length;
  return { done, total, ratio: total ? done / total : 1, complete: done === total };
}

export function startHere(f: EngineFacts): HubId | null {
  for (const id of SETUP_ORDER) if (!hubProgress(HUB_BY_ID[id], f).complete) return id;
  return null;
}

export interface HubLive {
  steps: number;
  applies: number;
  connected: string[];
  missing: string[];
  events30d: number;
  openTasks: number;
  /** Tasks waiting for a person (approvals and decisions). */
  waitingForYou: number;
  /** Tasks agents have open or are working on now (open tasks not assigned to a person). */
  running: number;
  known: boolean;
}

/** The hub's live status for this business, from crm_process_map. */
export function hubLive(hub: Hub, live: Map<string, StepLive>): HubLive {
  const steps = hubSteps(hub);
  const out: HubLive = { steps: steps.length, applies: 0, connected: [], missing: [], events30d: 0, openTasks: 0, waitingForYou: 0, running: 0, known: false };
  const connected = new Set<string>();
  const missing = new Set<string>();
  for (const s of steps) {
    const l = live.get(s.id);
    if (!l) continue;
    out.known = true;
    if (l.applies !== false) out.applies++;
    out.events30d += l.events_30d;
    out.openTasks += l.open_tasks;
    out.waitingForYou += l.human_tasks;
    if (l.applies === false) continue;
    for (const v of l.vendors) {
      if (!v.required) continue;
      (v.connected ? connected : missing).add(v.name);
    }
  }
  for (const n of connected) missing.delete(n);
  out.running = Math.max(0, out.openTasks - out.waitingForYou);
  out.connected = [...connected].sort();
  out.missing = [...missing].sort();
  if (!out.known) out.applies = steps.length;
  return out;
}

/** Whether a satellite's channel or source is used by this business. Unknown counts as on. */
export function satelliteOn(sat: Satellite, live: Map<string, StepLive>): boolean {
  const rows = sat.steps.map((id) => live.get(id)).filter((r): r is StepLive => Boolean(r));
  if (!rows.length) return true;
  return rows.some((r) => r.applies !== false);
}

/** How busy a hub's cables look: a gentle baseline so the map never looks dead, rising with real activity. */
export function flowWeight(events30d: number): number {
  return 0.35 + Math.min(1, Math.log10(1 + Math.max(0, events30d)) / 2.4) * 0.65;
}

async function settle<T>(f: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await f();
  } catch {
    return fallback;
  }
}

/** What the business has set up, read once for the module status cards (each read that fails counts as nothing). */
export async function loadEngineFacts(crm: Crm, config: JsonObject | null, onboarded: boolean): Promise<EngineFacts> {
  const [state, checklist, documents, tags, sequences, articles, live, meta] = await Promise.all([
    settle(() => crm.firstLoginState(), null),
    settle(() => crm.checklist(), []),
    settle(() => crm.documents(), []),
    settle(() => crm.tagDefinitions(), []),
    settle(() => crm.sequences(), []),
    settle(() => crm.articles(1), []),
    settle(() => crm.processMap(), [] as StepLive[]),
    settle(() => crm.metaAccounts(), []),
  ]);
  const vendors: Record<string, VendorStatus> = {};
  for (const c of checklist ?? []) vendors[c.vendor_key] = c.status;
  const approved = Boolean(state?.config_approved);
  return {
    onboarded,
    intake: hasIntake(documents ?? []),
    configApproved: approved,
    accountsRequired: state?.accounts_required ?? 0,
    accountsReady: state?.accounts_ready ?? 0,
    vendors,
    config: approved && config && Object.keys(config).length ? config : null,
    tagKeys: (tags ?? []).map((t) => t.key),
    sequences: (sequences ?? []).length,
    articles: (articles ?? []).length,
    live: new Map((live ?? []).map((r) => [r.step_id, r])),
    metaConnected: (meta ?? []).some((a) => a.status === "connected"),
  };
}
